import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { LayoutDashboard, ClipboardList, Calendar, Briefcase, GraduationCap, MessageSquareQuote, Mail, CalendarPlus, LogOut, ShieldAlert, BadgeDollarSign, KeyRound } from "lucide-react";
import { api, fmtDate, fmtTime, fmtMoney } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LoginCard } from "./Login";
import { AdminTable } from "@/components/admin/AdminTable";
import { EventManager } from "@/components/admin/EventManager";
import { ServiceFees } from "@/components/admin/ServiceFees";
import { ChangePassword } from "@/components/admin/ChangePassword";
import { AdminLoginForm } from "@/components/auth/AdminLoginForm";

const contact = (r) => <><div>{r.full_name || r.name}</div><div className="text-xs text-white/45">{r.email}</div>{r.phone && <div className="text-xs text-white/45">{r.phone}</div>}</>;
const when = (r) => new Date(r.created_at).toLocaleDateString();
const paid = (r) => (r.fee ? (r.paid ? `${fmtMoney(r.amount_paid)} paid` : `${fmtMoney(r.fee)} unpaid`) : "Free");

const SECTIONS = {
  intakes: { label: "Client Intakes", icon: ClipboardList, statuses: ["new", "contacted", "in-progress", "completed", "archived"],
    columns: [{ key: "c", label: "Client", render: contact }, { key: "services", label: "Services", render: (r) => r.services.join(", ") }, { key: "goals", label: "Goals" }, { key: "d", label: "Received", render: when }] },
  appointments: { label: "Appointments", icon: Calendar, statuses: ["awaiting_payment", "pending", "confirmed", "completed", "cancelled"],
    columns: [{ key: "c", label: "Client", render: contact }, { key: "service", label: "Service" }, { key: "w", label: "When", render: (r) => `${fmtDate(r.date)} · ${fmtTime(r.time)}` }, { key: "meeting_type", label: "Type" }, { key: "p", label: "Payment", render: paid }, { key: "notes", label: "Notes" }] },
  applications: { label: "Job Applications", icon: Briefcase, statuses: ["new", "reviewing", "interview", "hired", "declined"],
    columns: [{ key: "c", label: "Applicant", render: contact }, { key: "position", label: "Position" }, { key: "experience", label: "Experience" }, { key: "why_b2r", label: "Why B2R" }, { key: "d", label: "Received", render: when }] },
  registrations: { label: "Academy Registrations", icon: GraduationCap, statuses: ["awaiting_payment", "registered", "attended", "no-show", "cancelled"],
    columns: [{ key: "c", label: "Attendee", render: contact }, { key: "event_title", label: "Event" }, { key: "e", label: "Date", render: (r) => fmtDate(r.event_date) }, { key: "p", label: "Payment", render: paid }] },
  testimonials: { label: "Testimonials", icon: MessageSquareQuote, statuses: ["pending", "approved", "rejected"],
    columns: [{ key: "c", label: "From", render: contact }, { key: "rating", label: "Rating", render: (r) => "★".repeat(r.rating) }, { key: "message", label: "Message" }] },
  contacts: { label: "Messages", icon: Mail, statuses: ["new", "replied", "closed"],
    columns: [{ key: "c", label: "From", render: contact }, { key: "subject", label: "Subject" }, { key: "message", label: "Message" }, { key: "d", label: "Received", render: when }] },
};

const Overview = () => {
  const { data: s } = useQuery({ queryKey: ["admin", "stats"], queryFn: () => api.get("/admin/stats").then((r) => r.data) });
  if (!s) return <p className="text-white/50">Loading…</p>;
  const cards = [["New Intakes", s.new_intakes], ["Pending Appointments", s.pending_appointments], ["Applications", s.applications], ["Academy Registrations", s.registrations], ["Pending Testimonials", s.pending_testimonials], ["Messages", s.contacts], ["Events", s.events], ["Client Accounts", s.clients]];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10" data-testid="admin-stats">
      {cards.map(([l, v]) => (
        <div key={l} className="bg-[#0A0A0A] p-6" data-testid={`admin-stat-${l.toLowerCase().replace(/\s/g, "-")}`}>
          <p className="text-[11px] tracking-[0.18em] uppercase text-white/45">{l}</p>
          <p className="mt-3 font-display text-5xl text-[#D4AF37]">{v}</p>
        </div>
      ))}
    </div>
  );
};

const NAV = [["overview", "Overview", LayoutDashboard], ...Object.entries(SECTIONS).map(([k, v]) => [k, v.label, v.icon]), ["events", "Manage Events", CalendarPlus], ["pricing", "Consultation Prices", BadgeDollarSign], ["account", "Account & Password", KeyRound]];
const TITLES = { overview: "Overview", events: "Manage Events", pricing: "Consultation Prices", account: "Account & Password" };

const Dashboard = ({ user }) => {
  const [tab, setTab] = useState("overview");
  const { logout } = useAuth();
  const sec = SECTIONS[tab];
  return (
    <div className="pt-20 min-h-screen grid lg:grid-cols-[260px_1fr]" data-testid="admin-dashboard">
      <aside className="border-r border-white/10 bg-black lg:min-h-[calc(100vh-5rem)] p-4 flex lg:flex-col gap-1 overflow-x-auto">
        <p className="hidden lg:block eyebrow px-3 py-4">Admin Console</p>
        {NAV.map(([k, l, I]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`admin-nav-${k}`}
            className={`flex items-center gap-3 px-3 py-2.5 text-sm whitespace-nowrap transition-colors duration-200 border-l-2 ${tab === k ? "border-[#D4AF37] bg-[#2E0F3E] text-white" : "border-transparent text-white/60 hover:text-white"}`}>
            <I className="w-4 h-4" />{l}
          </button>
        ))}
        <button onClick={logout} className="lg:mt-auto flex items-center gap-3 px-3 py-2.5 text-sm text-white/50 hover:text-white" data-testid="admin-logout-button"><LogOut className="w-4 h-4" />Sign Out</button>
      </aside>
      <section className="p-6 md:p-10 bg-[#0A0A0A] min-w-0">
        <div className="flex justify-between items-end mb-8">
          <div><p className="eyebrow mb-2">Welcome, {user.name?.split(" ")[0]}</p><h1 className="text-4xl font-medium" data-testid="admin-section-title">{TITLES[tab] || sec.label}</h1></div>
        </div>
        {tab === "overview" && <Overview />}
        {tab === "events" && <EventManager />}
        {tab === "pricing" && <ServiceFees />}
        {tab === "account" && <ChangePassword email={user.email} />}
        {sec && <AdminTable key={tab} collection={tab} columns={sec.columns} statuses={sec.statuses} />}
      </section>
    </div>
  );
};

export default function Admin() {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen" />;
  if (!user) return (
    <LoginCard title="Admin Console" sub="Restricted area. Sign in with an authorized B2R admin account." path="/admin">
      <AdminLoginForm />
    </LoginCard>
  );
  if (user.role !== "admin") return (
    <div className="min-h-screen flex items-center justify-center px-6" data-testid="admin-access-denied">
      <div className="lux-card p-12 max-w-md text-center"><ShieldAlert className="w-12 h-12 text-[#D4AF37] mx-auto" /><h1 className="mt-6 text-4xl font-medium">Access restricted</h1><p className="mt-3 text-white/60 text-sm">You're signed in as <span className="text-white" data-testid="admin-denied-email">{user.email}</span>, which doesn't have admin access.</p><Link to="/portal" className="btn-outline-gold mt-8">Go to Client Portal</Link></div>
    </div>
  );
  return <Dashboard user={user} />;
}
